import { useEffect, useState } from "react";

import { Classes } from "@blueprintjs/core";
import styled from "@emotion/styled";
import { differenceInSeconds } from "date-fns";
import { useMediaQuery } from "react-responsive";

import sparkleAvatar from "@/../../hypetrain/static/img/sparkle-avatar.svg";
import { EmbedSvg } from "@/components/helpers/ui/EmbedSvg";
import { Box, Flex } from "@/components/layout/flexbox";
import { isXsQuery } from "@/components/layout/Media";
import {
  PrimaryButton,
  StyledButton,
} from "@/components/pieces/button/PrimaryButton";
import { Avatar } from "@/components/pieces/users/Avatar";
import { BRAND_PURPLE, CARD_CLASSES, SUBTEXT_COLOR } from "@/css/constants";
import { OrderItem } from "@/types/products";
import { formatDate, formatMinutes } from "@/utils/datetime";

function useCountdown(endTime: Date, onCompleted: () => void) {
  const [timeLeft, setTimeLeft] = useState(
    differenceInSeconds(endTime, new Date()),
  );

  useEffect(() => {
    const timer = setInterval(() => {
      const newTimeLeft = differenceInSeconds(endTime, new Date());
      if (newTimeLeft < 0) {
        onCompleted();
      }

      setTimeLeft(newTimeLeft);
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  return timeLeft;
}

interface UnpaidOrderProps {
  orderItems: OrderItem[];
  onPressPayNow: () => void;
  onPressCancel: () => void;
  cancelTime: Date;
  onExpiredMeeting: () => void;
}

export function UnpaidOrder({
  orderItems = [],
  onPressPayNow,
  onPressCancel,
  cancelTime,
  onExpiredMeeting,
}: UnpaidOrderProps) {
  const meeting = orderItems[0]?.meetings[0];
  const coach = meeting?.coach;
  const eventAt = meeting?.eventAt || "";
  const name = coach?.name;
  const timeLeft = useCountdown(cancelTime, onExpiredMeeting);
  const isXs = useMediaQuery(isXsQuery);

  if (!coach) return <Box mt={24} height={300} className={Classes.SKELETON} />;

  return (
    <StyledComponent
      className={CARD_CLASSES}
      backgroundColor="#FFF"
      px={[3, 3, 3, 4]}
    >
      <Flex pl={1} pr={[3, 4]} py={4}>
        <Flex justifyContent="center" className="avatar-container">
          <Avatar user={{ name }} size={isXs ? 70 : 140} />
          <EmbedSvg
            width={isXs ? 75 : 150}
            height={isXs ? 75 : 150}
            src={sparkleAvatar}
            style={{ position: "absolute" }}
          />
        </Flex>
        <Flex flex={3} flexDirection="column" pr={3} pl={3}>
          <h4>{`Finish checkout to confirm your session with ${name}`}</h4>
          <Box color={SUBTEXT_COLOR}>{`A session on ${formatDate(
            eventAt,
            "MMMM do, yyyy 'at' hh:mm a z",
          )} is currently reserved for you.`}</Box>
          <Flex
            mt={3}
            alignItems="center"
            flexDirection={isXs ? "column" : "row"}
          >
            <Box width={[1, 225]} mr={3}>
              <PrimaryButton
                onClick={onPressPayNow}
                style={{ width: "100%", height: "50px" }}
              >
                Pay Now
              </PrimaryButton>
            </Box>
            <Box>
              <StyledButton
                onClick={onPressCancel}
                style={{ width: "100%", height: "50px" }}
              >
                Cancel
              </StyledButton>
            </Box>
          </Flex>
          <Box mt={2}>
            Reservation released in&nbsp;
            <span className="countdown-text">{formatMinutes(timeLeft)}</span>
          </Box>
        </Flex>
      </Flex>
    </StyledComponent>
  );
}

const StyledComponent = styled(Box)`
  margin: 36px 0;

  .info-text {
    color: rgb(119, 131, 151);
  }

  .avatar-container {
    position: relative;
  }

  .sparkle {
    position: absolute;
  }

  .sparkle_1 {
    top: 0;
    right: 0;
  }

  .sparkle_2 {
    top: 20%;
    left: 0;
  }

  .sparkle_3 {
    bottom: 0;
    left: 10%;
  }

  .countdown-text {
    color: ${BRAND_PURPLE};
  }
`;
